<template>
  <div id="app" class="min-h-screen bg-cream-200">
    <nav
      class="sticky top-0 z-50 bg-primary-800 shadow-warm border-b border-primary-700/60"
    >
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="flex h-20 items-center justify-between">
          <router-link
            to="/"
            class="group flex items-center gap-3 transition-transform duration-300 hover:-translate-y-0.5"
          >
            <img
              src="./assets/images/logo.jpg"
              alt="Roomsvile Logo"
              class="h-12 w-12 rounded-lg border-2 border-gold-400/50 object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <span
              class="font-display text-2xl font-semibold tracking-tight text-cream-50"
              >Roomsvile</span
            >
          </router-link>

          <!-- Desktop nav -->
          <ul class="hidden items-center gap-1 lg:flex">
            <li>
              <router-link to="/" class="nav-link">
                <svg
                  class="mr-1.5 h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
                  ></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
                Home
              </router-link>
            </li>
            <li>
              <router-link to="/about" class="nav-link">
                <svg
                  class="mr-1.5 h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                About
              </router-link>
            </li>
            <li>
              <router-link to="/contact" class="nav-link">
                <svg
                  class="mr-1.5 h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                  ></path>
                </svg>
                Contact
              </router-link>
            </li>

            <template v-if="!isAuthenticated">
              <li>
                <router-link to="/login" class="nav-link">
                  <svg
                    class="mr-1.5 h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                    <polyline points="10 17 15 12 10 7"></polyline>
                    <line x1="15" y1="12" x2="3" y2="12"></line>
                  </svg>
                  Log In
                </router-link>
              </li>
              <li class="ml-2">
                <router-link
                  to="/register"
                  class="flex items-center rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white shadow-warm-sm transition-colors duration-200 hover:bg-primary-600"
                >
                  <svg
                    class="mr-1.5 h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="8.5" cy="7" r="4"></circle>
                    <line x1="20" y1="8" x2="20" y2="14"></line>
                    <line x1="17" y1="11" x2="23" y2="11"></line>
                  </svg>
                  Sign Up
                </router-link>
              </li>
            </template>

            <li v-else class="relative ml-2">
              <button
                type="button"
                class="nav-link"
                @click="accountMenuOpen = !accountMenuOpen"
                :aria-expanded="accountMenuOpen"
              >
                Hi, {{ currentUser?.firstName }}
                <svg
                  class="ml-1.5 h-3.5 w-3.5 transition-transform duration-200"
                  :class="{ 'rotate-180': accountMenuOpen }"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>

              <div
                v-if="accountMenuOpen"
                class="fixed inset-0 z-40"
                @click="accountMenuOpen = false"
              ></div>

              <transition
                enter-active-class="transition duration-150 ease-out"
                enter-from-class="opacity-0 -translate-y-1"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition duration-100 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <div
                  v-if="accountMenuOpen"
                  class="absolute right-0 z-50 mt-2 w-48 overflow-hidden rounded-xl border border-sand-200 bg-white py-1 shadow-warm"
                >
                  <router-link
                    to="/profile"
                    class="block px-4 py-2 text-sm text-ink-700 hover:bg-cream-100"
                    @click="accountMenuOpen = false"
                    >My Account</router-link
                  >
                  <router-link
                    to="/bookings"
                    class="block px-4 py-2 text-sm text-ink-700 hover:bg-cream-100"
                    @click="accountMenuOpen = false"
                    >My Bookings</router-link
                  >
                  <router-link
                    to="/favourites"
                    class="block px-4 py-2 text-sm text-ink-700 hover:bg-cream-100"
                    @click="accountMenuOpen = false"
                    >My Favourites</router-link
                  >
                  <hr class="my-1 border-sand-200" />
                  <button
                    type="button"
                    class="block w-full px-4 py-2 text-left text-sm text-ink-700 hover:bg-cream-100"
                    @click="handleLogout"
                  >
                    Log Out
                  </button>
                </div>
              </transition>
            </li>
          </ul>

          <!-- Mobile toggle -->
          <button
            type="button"
            class="flex h-10 w-10 items-center justify-center rounded-lg text-cream-50 transition-colors hover:bg-white/10 lg:hidden"
            @click="mobileMenuOpen = !mobileMenuOpen"
            :aria-expanded="mobileMenuOpen"
            aria-label="Toggle navigation"
          >
            <svg
              class="h-6 w-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path v-if="!mobileMenuOpen" d="M3 12h18M3 6h18M3 18h18"></path>
              <path v-else d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Mobile menu -->
        <transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 -translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div v-if="mobileMenuOpen" class="space-y-1 pb-4 lg:hidden">
            <router-link
              to="/"
              class="mobile-nav-link"
              @click="mobileMenuOpen = false"
              >Home</router-link
            >
            <router-link
              to="/about"
              class="mobile-nav-link"
              @click="mobileMenuOpen = false"
              >About</router-link
            >
            <router-link
              to="/contact"
              class="mobile-nav-link"
              @click="mobileMenuOpen = false"
              >Contact</router-link
            >
            <template v-if="!isAuthenticated">
              <router-link
                to="/login"
                class="mobile-nav-link"
                @click="mobileMenuOpen = false"
                >Log In</router-link
              >
              <router-link
                to="/register"
                class="mobile-nav-link"
                @click="mobileMenuOpen = false"
                >Sign Up</router-link
              >
            </template>
            <template v-else>
              <router-link
                to="/profile"
                class="mobile-nav-link"
                @click="mobileMenuOpen = false"
                >My Account</router-link
              >
              <router-link
                to="/bookings"
                class="mobile-nav-link"
                @click="mobileMenuOpen = false"
                >My Bookings</router-link
              >
              <router-link
                to="/favourites"
                class="mobile-nav-link"
                @click="mobileMenuOpen = false"
                >My Favourites</router-link
              >
              <button
                type="button"
                class="mobile-nav-link w-full text-left"
                @click="
                  handleLogout();
                  mobileMenuOpen = false;
                "
              >
                Log Out
              </button>
            </template>
          </div>
        </transition>
      </div>
    </nav>

    <router-view />

    <footer class="bg-primary-900 py-14 text-cream-100">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <h3 class="font-display text-xl font-semibold text-cream-50">
              Roomsvile
            </h3>
            <p class="mt-3 text-sm leading-relaxed text-primary-200">
              Your trusted partner for hotels and serviced apartments.
            </p>
          </div>
          <div>
            <h4
              class="text-sm font-semibold uppercase tracking-wide text-gold-400"
            >
              Quick Links
            </h4>
            <ul class="mt-4 space-y-2 text-sm text-primary-200">
              <li>
                <router-link to="/" class="footer-link">Home</router-link>
              </li>
              <li>
                <router-link to="/about" class="footer-link"
                  >About Us</router-link
                >
              </li>
              <li>
                <router-link to="/contact" class="footer-link"
                  >Contact Us</router-link
                >
              </li>
            </ul>
          </div>
          <div>
            <h4
              class="text-sm font-semibold uppercase tracking-wide text-gold-400"
            >
              Services
            </h4>
            <ul class="mt-4 space-y-2 text-sm text-primary-200">
              <li v-for="service in footerServices" :key="service">
                <router-link to="/about#services" class="footer-link">
                  {{ service }}
                </router-link>
              </li>
            </ul>
          </div>
          <div>
            <h4
              class="text-sm font-semibold uppercase tracking-wide text-gold-400"
            >
              Contact
            </h4>
            <ul class="mt-4 space-y-2 text-sm text-primary-200">
              <li>Email: info@roomsvile.com</li>
              <li>Phone: +234 (0) 808 7102 974</li>
            </ul>
          </div>
        </div>
        <hr class="my-8 border-primary-700" />
        <div class="text-center text-sm text-primary-300">
          <p>&copy; 2025 Roomsvile. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useRoute } from "vue-router";
import { useHead } from "@vueuse/head";
import { useAuthStore } from "@/stores/auth.store";
import {
  SITE_NAME,
  SITE_URL,
  generateOrganizationStructuredData,
} from "@/composables/useSEO";

export default defineComponent({
  setup() {
    const route = useRoute();

    // Defaults for every route from its meta. Pages that call useSEO override
    // title/description/canonical; robots always comes from here so private
    // pages stay out of search results.
    useHead(
      computed(() => {
        const title = (route.meta.title as string) || SITE_NAME;
        const description = (route.meta.description as string) || "";
        const canonical = `${SITE_URL}${route.path === "/" ? "/" : route.path}`;

        return {
          htmlAttrs: { lang: "en" },
          title,
          meta: [
            { name: "description", content: description },
            { property: "og:title", content: title },
            { property: "og:description", content: description },
            { property: "og:url", content: canonical },
            { name: "twitter:title", content: title },
            { name: "twitter:description", content: description },
            {
              name: "robots",
              content: route.meta.noindex
                ? "noindex, follow"
                : "index, follow, max-image-preview:large",
            },
          ],
          link: [{ rel: "canonical", href: canonical }],
          script: [
            {
              key: "ld-json-organization",
              type: "application/ld+json",
              children: JSON.stringify(generateOrganizationStructuredData()),
            },
          ],
        };
      })
    );
  },
  data() {
    return {
      mobileMenuOpen: false,
      accountMenuOpen: false,
      // Mirrors the services listed on the About page
      footerServices: [
        "Hotel Reservations",
        "Short-Let Apartments",
        "Serviced Apartments",
        "Property Management",
      ],
    };
  },
  computed: {
    authStore() {
      return useAuthStore();
    },
    isAuthenticated() {
      return this.authStore.isAuthenticated;
    },
    currentUser() {
      return this.authStore.user;
    },
  },
  methods: {
    async handleLogout() {
      await this.authStore.logout();
      this.$router.push("/");
    },
  },
  mounted() {
    this.authStore.initializeAuth();
  },
});
</script>

<style>
#app {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.9rem;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(252, 248, 241, 0.8);
  transition: color 0.2s ease, background-color 0.2s ease;
}

.nav-link:hover {
  color: #fcf8f1;
  background-color: rgba(255, 255, 255, 0.08);
}

.nav-link.router-link-exact-active {
  color: #fcf8f1;
  background-color: rgba(255, 255, 255, 0.12);
  font-weight: 600;
}

.mobile-nav-link {
  display: block;
  border-radius: 0.5rem;
  padding: 0.65rem 0.9rem;
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(252, 248, 241, 0.85);
  transition: background-color 0.2s ease;
}

.mobile-nav-link:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.mobile-nav-link.router-link-exact-active {
  background-color: rgba(255, 255, 255, 0.12);
  color: #fcf8f1;
}

.footer-link {
  transition: color 0.2s ease;
}

.footer-link:hover {
  color: #dbeafe;
}
</style>
